import React, { useEffect, useMemo, useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import SupervisorBankTicketsView from './SupervisorBankTicketsView';
import { fetchSupervisorBankMonitoring } from '../../api/supervisor';
import './SupervisorBanquesConnecteResults.css';

const statutToStatus = {
  'En ligne': 'online',
  'Deconnecte': 'offline',
  "N'a pas connecte": 'never',
};

const formatMoney = (v) => Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 });
const formatTime = (iso) => (iso ? new Date(iso).toLocaleTimeString() : '');

// Meme derivation client-side du statut que BanquesConnecteResults.jsx (pas de
// heartbeat reel cote app mobile) — cf. SupervisorBankMonitoringView.
const getStatus = (row, offlineMinutes) => {
  if (!row.last_movement) return 'never';
  const diffMin = (Date.now() - new Date(row.last_movement).getTime()) / 60000;
  return diffMin <= Number(offlineMinutes) ? 'online' : 'offline';
};

const SupervisorBanquesConnecteResults = ({ lottery, groupId, date, statut, inColumn, offlineMinutes, onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchSupervisorBankMonitoring({ date, tirage: lottery, group: groupId })
      .then((data) => { if (!cancelled) setRows(data); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les banques — verifie que le serveur backend tourne.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, lottery, groupId]);

  const activeRows = useMemo(
    () => rows.filter((r) => r.sale_enabled).map((r) => ({ ...r, status: getStatus(r, offlineMinutes) })),
    [rows, offlineMinutes]
  );
  const inactiveCount = rows.length - activeRows.length;

  const statusFilter = statutToStatus[statut] || null;
  const filtered = statusFilter ? activeRows.filter((r) => r.status === statusFilter) : activeRows;

  const columns = useMemo(() => {
    const n = inColumn ? Math.min(1, filtered.length) || 1 : Math.min(5, filtered.length) || 1;
    const chunkSize = Math.ceil(filtered.length / n) || 1;
    const cols = [];
    for (let i = 0; i < n; i++) {
      const slice = filtered.slice(i * chunkSize, (i + 1) * chunkSize);
      if (slice.length) cols.push(slice);
    }
    return cols;
  }, [filtered, inColumn]);

  const sumTotal = (list) => list.reduce((s, r) => s + Number(r.total), 0);
  const onlineRows = activeRows.filter((r) => r.status === 'online');
  const offlineRows = activeRows.filter((r) => r.status === 'offline');
  const neverRows = activeRows.filter((r) => r.status === 'never');

  const handleEnter = (row, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ row, x: rect.left, y: rect.bottom });
  };

  if (selectedBank !== null) {
    return (
      <SupervisorBankTicketsView
        bank={selectedBank}
        date={date}
        onBack={() => setSelectedBank(null)}
      />
    );
  }

  return (
    <SupervisorLayout>
      <button className="svbcr-back-btn" onClick={onBack}>Arriere</button>

      <div className="svbcr-header">
        <h2 className="svbcr-title">Monitoring de la Banque</h2>
      </div>

      {error && <p className="svbcr-error">{error}</p>}
      {loading && <p className="svbcr-loading">Chargement...</p>}

      {!loading && !error && (
        <>
          <div className="svbcr-stats-bar">
            <span className="svbcr-chip svbcr-chip-inactif">{inactiveCount}</span> = Inactif
            <span className="svbcr-chip svbcr-chip-online">{onlineRows.length}</span> = En ligne <strong>{formatMoney(sumTotal(onlineRows))}</strong>
            <span className="svbcr-chip svbcr-chip-offline">{offlineRows.length}</span> = Deconnecte <strong>{formatMoney(sumTotal(offlineRows))}</strong>
            <span className="svbcr-chip svbcr-chip-never">{neverRows.length}</span> = N'a pas connectes
            <span className="svbcr-chip svbcr-chip-total">{activeRows.length}</span> = Total <strong>{formatMoney(sumTotal(activeRows))}</strong>
            <span className="svbcr-scope-note">In {rows.length} Banques</span>
          </div>

          <div className="svbcr-grid">
            {columns.length === 0 && <p className="svbcr-empty">Aucune banque a afficher.</p>}
            {columns.map((col, ci) => (
              <table key={ci} className="svbcr-table">
                <thead>
                  <tr>
                    <th>Banque</th>
                    <th>Dernier mouvement</th>
                    <th>Ventes Total</th>
                  </tr>
                </thead>
                <tbody>
                  {col.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span
                          className={`svbcr-numero svbcr-numero-${row.status}`}
                          onMouseEnter={(e) => handleEnter(row, e)}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={() => setSelectedBank(row)}
                        >
                          {row.seq}
                        </span>
                      </td>
                      <td className={row.status === 'online' ? 'svbcr-time-online' : ''}>{formatTime(row.last_movement)}</td>
                      <td>{formatMoney(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </>
      )}

      {tooltip && (
        <div className="svbcr-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="svbcr-tooltip-name">{tooltip.row.name}</div>
          <div>Telephone: {tooltip.row.phone || '.'}</div>
          <div>Total des Tickets: {tooltip.row.ticket_count}</div>
          <div>Ventes Total: {formatMoney(tooltip.row.total)}</div>
          <div>Last Sale Temps: {formatTime(tooltip.row.last_movement) || '-'}</div>
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorBanquesConnecteResults;
